import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AlertTriangle } from 'lucide-react';

const MissingDataHeatmap = ({ data, height = null }) => {
 
  const containerRef = useRef();
  const ref = useRef();
  const { isDarkMode } = useTheme();
  const [width, setWidth] = useState(600);

  // Effect to handle responsive width with ResizeObserver
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth > 0) {
          setWidth(newWidth);
        }
      }
    };

    // Initial width calculation
    updateWidth();
    
    // Use ResizeObserver for better performance
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          const newWidth = entries[0].contentRect.width;
          if (newWidth > 0) {
            setWidth(newWidth);
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize event
    const handleResize = () => {
      requestAnimationFrame(updateWidth);
    };
    window.addEventListener('resize', handleResize);

    // Recalculate after a short delay to handle initial render
    const timeout = setTimeout(updateWidth, 100);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {

    if (!data || !ref.current) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const collectors = Array.from(new Set(data.map(d => d.collector)));
    const variables = Array.from(new Set(data.map(d => d.variable)));
    const collectorsOnYAxis = variables.length >= collectors.length;

    let xDomain, yDomain, xLabel, yLabel;
    
    if (collectorsOnYAxis) {
      xDomain = variables;
      yDomain = collectors;
      xLabel = 'Variables';
      yLabel = 'Data Collectors';
    } else {
      xDomain = collectors;
      yDomain = variables;
      xLabel = 'Data Collectors';
      yLabel = 'Variables';
    }

    const margin = { 
      top: 50, 
      right: 120,
      bottom: collectorsOnYAxis ? 180 : 130,
      left: collectorsOnYAxis ? 130 : 180
    };

    // Calculate cell size based on available width
    const availableWidth = width - margin.left - margin.right - 20; // 20px for padding
    const calculatedCellSize = Math.max(60, Math.min(60, availableWidth / xDomain.length));
    const cellSize = calculatedCellSize;
    
    const innerWidth = cellSize * xDomain.length;
    const innerHeight = cellSize * yDomain.length;
    const totalWidth = innerWidth + margin.left + margin.right;
    const calculatedHeight = innerHeight + margin.top + margin.bottom;
    
    const x = d3.scaleBand()
      .domain(xDomain)
      .range([0, innerWidth])
      .padding(0.08);

    const y = d3.scaleBand()
      .domain(yDomain)
      .range([0, innerHeight])
      .padding(0.08);

    const maxMissing = d3.max(data, d => d.missingPercentage);
    
    // Ensure minimum domain range to prevent color scale collapse when all values are 0
    // When maxMissing is 0, use 100 as the theoretical max so 0% maps to green
    const effectiveMax = maxMissing > 0 ? maxMissing : 100;
    
    // Modern color scale with better gradients
    const colorScale = d3.scaleLinear()
      .domain([0, effectiveMax / 4, effectiveMax * 0.75, effectiveMax])
      .range([
        '#10B981', // #34ced3ff',   // Green for 0% (complete data)
        '#c3d334ff', // Yellow-green
        '#FB923C',   // Orange
        '#EF4444'    // Red for high missingness
      ])
      .interpolate(d3.interpolateRgb.gamma(2.2));
    
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create modern tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'missing-heatmap-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', isDarkMode 
        ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.98) 0%, rgba(13, 14, 14, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)')
      .style('color', isDarkMode ? '#888888ff' : '#0F172A')
      .style('padding', '16px 20px')
      .style('border', isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)')
      .style('border-radius', '12px')
      .style('box-shadow', isDarkMode 
        ? '0 12px 40px rgba(0, 0, 0, 0.4)'
        : '0 12px 40px rgba(0, 0, 0, 0.15)')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('backdrop-filter', 'blur(20px)');

    
    // Draw cells with modern styling
    g.selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', d => collectorsOnYAxis ? x(d.variable) : x(d.collector))
      .attr('y', d => collectorsOnYAxis ? y(d.collector) : y(d.variable))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', d => colorScale(d.missingPercentage))
      .attr('rx', 6)
      .attr('ry', 6)
      .style('cursor', 'pointer')
      .style('transition', 'all 0.2s ease')
      .style('filter', d => d.missingPercentage > 0 
        ? `drop-shadow(0 2px 8px ${colorScale(d.missingPercentage)}40)`
        : 'none')
      .style('transform-origin', 'center')
      .style('transform-box', 'fill-box')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('rx', 8)
          .attr('ry', 8)
          .style('transform', 'scale(1.12)')
          .attr('stroke-width', 3)
          .style('filter', `drop-shadow(0 4px 12px ${colorScale(d.missingPercentage)}60)`);
          
        tooltip.style('visibility', 'visible')
          .html(`
            <div style="margin-bottom: 12px; font-weight: 700; font-size: 14px; border-bottom: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}; padding-bottom: 8px;">
              Cell Details
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Collector:</span>
              <span style="font-weight: 600;">${d.collector}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Variable:</span>
              <span style="font-weight: 600;">${d.variable}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Missing:</span>
              <span style="font-weight: 700; color: ${colorScale(d.missingPercentage)};">${d.missingPercentage.toFixed(1)}%</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Count:</span>
              <span style="font-weight: 600;">${d.missingCount}/${d.totalCount}</span>
            </div>
          `);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 10) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('rx', 6)
          .attr('ry', 6)
          .style('transform', 'scale(1)')
          .style('filter', d => d.missingPercentage > 0 
            ? `drop-shadow(0 2px 8px ${colorScale(d.missingPercentage)}40)`
            : 'none');
        tooltip.style('visibility', 'hidden');
      });

    // Add cell value labels with better contrast - adjust font size based on cell size
    const fontSize = Math.max(10, Math.min(12, cellSize / 5));
    
    g.selectAll('text.cell-value')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'cell-value')
      .attr('x', d => (collectorsOnYAxis ? x(d.variable) : x(d.collector)) + x.bandwidth() / 2)
      .attr('y', d => (collectorsOnYAxis ? y(d.collector) : y(d.variable)) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-size', `${fontSize}px`)
      .style('font-weight', '700')
      .style('letter-spacing', '-0.01em')
      .style('fill', d => {
        const color = d3.rgb(colorScale(d.missingPercentage));
        const brightness = (color.r * 299 + color.g * 587 + color.b * 114) / 1000;
        return brightness < 128 ? '#101310ff' : '#17171B';
      })
      .style('pointer-events', 'none')
      .text(d => d.missingPercentage > 0 ? d.missingPercentage.toFixed(0) + '%' : '0');

    // Add axes with modern styling
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x));
    
    const maxXLabelLength = Math.max(...xDomain.map(d => String(d).length));
    const rotateXLabels = maxXLabelLength > 8 || xDomain.length > 8;
    
    xAxis.selectAll('text')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('font-size', '12px')
      .style('font-weight', '500');
    
    if (rotateXLabels) {
      xAxis.selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0em')
        .attr('transform', 'rotate(-90)');
    } else {
      xAxis.selectAll('text')
        .style('text-anchor', 'middle')
        .attr('dy', '1em');
    }
    
    xAxis.selectAll('path, line')
      .style('stroke', isDarkMode ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0)')    // x-axis line hidden in dark mode
      .style('stroke-width', '2px');

    const yAxis = g.append('g')
      .call(d3.axisLeft(y));
    
    yAxis.selectAll('text')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('font-size', '12px')
      .style('font-weight', '500');
    
    yAxis.selectAll('path, line')
      .style('stroke', isDarkMode ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0)')
      .style('stroke-width', '2px');

    // Add axis labels with better typography
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + margin.bottom - 20)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', '700')
      .style('fill', isDarkMode ? '#E2E8F0' : '#1E293B')
      .style('letter-spacing', '0.02em')
      .text(xLabel);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -margin.left + 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', '700')
      .style('fill', isDarkMode ? '#E2E8F0' : '#1E293B')
      .style('letter-spacing', '0.02em')
      .text(yLabel);

    // Add modern color legend with gradient
    const legendWidth = 24;
    const legendHeight = Math.min(innerHeight * 0.6, 180);
    const legendX = totalWidth - margin.right + 30;
    const legendY = margin.top + (innerHeight - legendHeight) / 2;

    const defs = svg.append('defs');
    const gradientId = `missing-heatmap-gradient-${Math.random().toString(36).substr(2, 9)}`;
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '100%')
      .attr('y2', '0%');

    gradient.selectAll('stop')
      .data([
        { offset: '0%', color: isDarkMode ? '#10B981' : '#10B981' }, // { offset: '0%', color: '#34ced3ff' },     // Green for 0% (complete data)
        { offset: '25%', color: '#c3d334ff' },  // Yellow-green
        { offset: '75%', color: '#FB923C' },    // Orange
        { offset: '100%', color: '#EF4444' }    // Red for high missingness
      ])
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color);

    const legend = svg.append('g');
    
    // Add shadow to legend
    const shadowId = `legend-shadow-${Math.random().toString(36).substr(2, 9)}`;
    const filter = defs.append('filter')
      .attr('id', shadowId);
    
    filter.append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 2)
      .attr('stdDeviation', 4)
      .attr('flood-opacity', 0.15);
    
    legend.append('rect')
      .attr('x', legendX)
      .attr('y', legendY)
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .attr('rx', 6)
      .attr('ry', 6)
      .style('fill', `url(#${gradientId})`)
      .style('filter', `url(#${shadowId})`);

    const legendLabels = [
      { y: legendY + legendHeight, text: '0%' },
      { y: legendY + legendHeight / 2, text: Math.round(maxMissing / 2).toString() + '%' },
      { y: legendY, text: maxMissing.toFixed(0) + '%' }
    ];

    legendLabels.forEach(label => {
      legend.append('text')
        .attr('x', legendX + legendWidth + 10)
        .attr('y', label.y)
        .style('text-anchor', 'start')
        .style('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
        .text(label.text);
    });

    legend.append('text')
      .attr('x', legendX + legendWidth / 2)
      .attr('y', legendY - 16)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', isDarkMode ? '#E2E8F0' : '#1E293B')
      .style('letter-spacing', '0.02em')
      .text('Missing %');

    svg.attr('width', totalWidth).attr('height', calculatedHeight);

    // Cleanup function
    return () => {
      tooltip.remove();
    };

  }, [data, width, height, isDarkMode]);

  if (!data || data.length === 0) {
    return (
      <div style={{
        width: '100%',
        height: height || 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDarkMode 
          ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.6) 0%, rgba(13, 14, 14, 0.4) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.8) 100%)',
        borderRadius: '20px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(10px)',
        color: isDarkMode ? '#94A3B8' : '#64748B',
        fontSize: '14px',
        fontWeight: '500'
      }}>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>No missing data available for heatmap</div>
        </div>
      </div>
    );
  }

  return (
    <div className='overflow-scroller'
      ref={containerRef} 
      style={{ 
        width: '100%',
        maxWidth: '100%',
        overflow: 'auto', // Allow horizontal scroll if needed on very small screens
        position: 'relative'
      }}
    >
      <svg 
        ref={ref} 
        style={{ 
          borderRadius: '12px',
          display: 'block',
          minWidth: '100%'
        }} 
      />
    </div>
  );

};
  
export default MissingDataHeatmap;